import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import {
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from '@mui/material';
import { IProfileInputModel, IProfileUpdateInputModel } from 'inputModels';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { IProfileLimitValueViewModel, IProfileViewModel } from 'viewModels';
import { useTheme } from 'styled-components';
import { v4 as uuid } from 'uuid';
import { ReactComponent as DeleteIcon } from '../../assets/icons/IconDelete.svg';
import { Button, InputFilter, MenuOptionLeftCompany, NavHeader } from '../../components';
import { AccordionExpanded } from '../../components/AccordionExpanded';
import CurrencyInput from '../../components/CurrencyInput';
import { ModalDeleteProfile } from '../../components/Modals/ModalDeleteProfile';
import { useApp, useAuth, useProfile } from '../../context';
import { useClients } from '../../context/clients';
import { useCorrespondences } from '../../context/correspondences';
import { useSpecialties } from '../../context/specialties';
import { CompanyController } from '../../controller/CompanyController';
import { labelProfileType, ProfileType, TutorialType } from '../../types/enums';
import { Body, Container, ContainerMap, Content, ContentMap, Header } from './styles';
import { useAlert } from '../../hooks/useAlert';
import { DropDownList } from '../../components/DropDownList';
import { slugify } from '../../utils/slugify';
import { InputAdd } from '../../components/Inputs/InputAdd';
import TitlePage from '../../components/TitlePage';
import JoyrideTour from '../../components/JoyrideTour';

export function Profiles() {
  const app = useApp();
  const alert = useAlert();
  const { user } = useAuth();
  const { colors } = useTheme();
  const { clients, setEnableClientsLoad } = useClients();
  const { specialties } = useSpecialties();
  const { profiles, setProfiles, isLoadingProfiles } = useProfile();
  const { correspondences } = useCorrespondences();
  const [selectedTypeProfile, setSelectedTypeProfile] = useState<ProfileType>();
  const [filterClients, setFilterClients] = useState('');
  const [filterSpecialties, setFilterSpecialties] = useState('');
  const [filterCorrespondences, setFilterCorrespondences] = useState('');
  const [filterProfiles, setFilterProfiles] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [messageError, setMessageError] = useState('');
  const [isChangingProfile, setIsChangingProfile] = useState(false);
  const [approvers, setApprovers] = useState<IProfileViewModel[]>([]);
  const [selectedIdClients, setSelectedIdClients] = useState<string[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<IProfileViewModel>();
  const [selectedIdSpecialties, setSelectedIdSpecialties] = useState<string[]>([]);
  const [displayModalDeleteProfile, setDisplayModalDeleteProfile] = useState(false);
  const [approverLimit, setApproverLimit] = useState<IProfileLimitValueViewModel>();
  const [selectedIdCorrespondences, setSelectedIdCorrespondences] = useState<string[]>([]);
  const [selectApproverLimitValue, setSelectLimitSelectValue] = useState('');
  const [isRunningTutorial, setIsRunningTutorial] = useState(false);
  const requestersToTutorial: IProfileViewModel[] = [
    {
      id: uuid(),
      name: 'Solicitante Tutorial',
      type: ProfileType.REQUESTER,
      approversId: [],
      clientsId: [],
      specialtiesId: [],
      correspondencesId: [],
      maxValue: 0,
      minValue: 0,
    },
  ];
  const [defaultValues] = useState({
    minValue: 0,
    maxValue: 0,
    selectApprover: '',
    selectApproverValue: '',
  });

  const { setValue, control } = useForm({
    reValidateMode: 'onChange',
    defaultValues,
  });

  const maxValueInput = useWatch({
    control,
    name: 'maxValue',
  });
  const minValueInput = useWatch({
    control,
    name: 'minValue',
  });

  const MenuProps = {
    PaperProps: {
      style: {
        backgroundColor: colors.background_white,
        color: colors.secondary,
      },
    },
  };

  const handleSelectApprovers = (id: string) => {
    const approver = profiles.find((p) => p.id === id);
    if (approver) {
      setApprovers((prev) => [...prev, approver].filter((v, i, a) => a.indexOf(v) === i));
    }
  };

  const handleSelectApproverLimitValue = (event: SelectChangeEvent<string>) => {
    const eventValue = event.target.value;
    setSelectLimitSelectValue(eventValue);
    const approver = profiles.find((p) => p.id === eventValue);
    const approverLimitValue: IProfileLimitValueViewModel | undefined = approver
      ? { id: approver.id as string, name: approver.name as string }
      : undefined;

    if (approverLimitValue) {
      setApproverLimit(approverLimitValue);
    }
    setSelectLimitSelectValue('');
  };

  const handleDeleteProfile = (id: string) => {
    setDisplayModalDeleteProfile(true);
    const findedProfile = profiles.find((p) => p.id === id);
    setSelectedProfile(findedProfile);
  };

  const handleAddProfile = (nameProfile: string, type: ProfileType) => {
    let hasError = false;
    if (nameProfile && type !== undefined) {
      const profile: IProfileInputModel = {
        name: nameProfile,
        companyId: user?.id,
        type,
      };
      const isExists = profiles.some((p) => p.name === profile.name && p.type === type);
      if (!isExists) {
        app.setLoading(true);
        CompanyController.createProfile(profile)
          .then((response) => {
            const newProfile: IProfileViewModel = {
              id: response.data,
              name: nameProfile,
              type,
              approversId: [],
              clientsId: [],
              specialtiesId: [],
              correspondencesId: [],
              maxValue: 0,
              minValue: 0,
              limitValueApproverId: '',
            };
            setProfiles((prev) => [...prev, newProfile]);
            alert.show({ message: 'Perfil cadastrado com sucesso!', type: 'success' });
          })
          .catch(() => {
            alert.show({ message: 'Erro ao cadastrar perfil!', type: 'error' });
          })
          .finally(() => {
            app.setLoading(false);
          });
      } else {
        setMessageError('Perfil já cadastrado');
        hasError = true;
      }
    } else {
      setMessageError('Nome do perfil é obrigatório');
      hasError = true;
    }
    return hasError;
  };

  const handleUpdateProfile = () => {
    if (!selectedProfile?.id) return;
    app.setLoading(true);
    const uptatedProfile: IProfileUpdateInputModel = {
      id: selectedProfile.id,
      name: selectedProfile?.name,
      companyId: user?.id,
      type: selectedProfile?.type,
      clientsId: selectedIdClients,
      specialtiesId: selectedIdSpecialties,
      correspondencesId: selectedIdCorrespondences,
      maxValue: maxValueInput,
      minValue: minValueInput,
      limitValueApproverId: approverLimit?.id,
      approversId: approvers.map((approver) => approver.id),
    };
    CompanyController.updateProfile(uptatedProfile)
      .then(() => {
        alert.show({ message: 'Perfil atualizado com sucesso!', type: 'success' });
      })
      .catch(() => {
        alert.show({ message: 'Erro ao atualizar perfil!', type: 'error' });
      })
      .finally(() => app.setLoading(false));
  };

  useEffect(() => {
    if (selectedProfile) {
      const profile = profiles.find((p) => p.id === selectedProfile?.id);
      if (profile) {
        setApprovers(profiles.filter((p) => profile.approversId?.some((a) => a === p.id)));
        const selectedProfileLimitValue = profiles.find(
          (p) => p.id === profile?.limitValueApproverId,
        );

        const approverLimits: IProfileLimitValueViewModel | undefined = selectedProfileLimitValue
          ? {
              id: selectedProfileLimitValue.id as string,
              name: selectedProfileLimitValue?.name as string,
            }
          : undefined;
        setApproverLimit(approverLimits);

        profile?.correspondencesId?.map((correspondenceId) => correspondenceId);
        setSelectedIdCorrespondences(
          profile?.correspondencesId?.map((correspondenceId) => correspondenceId) ?? [],
        );
        setSelectedIdClients(profile?.clientsId?.map((clientsId) => clientsId) ?? []);
        setSelectedIdSpecialties(
          profile?.specialtiesId?.map((specialtiesId) => specialtiesId) ?? [],
        );
        setValue('maxValue', profile?.maxValue || 0);
        setValue('minValue', profile?.minValue || 0);
      }
    }
  }, [profiles, selectedProfile, setValue]);

  const handleOpen = () => {
    setIsOpen(true);
    document.body.classList.add('no-scroll');
  };

  const handleClose = () => {
    setIsOpen(false);
    document.body.classList.remove('no-scroll');
  };

  const handleSelectProfile = (profileId: string) => {
    setIsChangingProfile(true);
    setSelectedProfile(profiles.find((p) => p.id === profileId));
  };
  const handleSelectAllClients = () => {
    if (selectedIdClients.length === clients?.length) {
      setSelectedIdClients([]);
    } else {
      setSelectedIdClients(clients?.map((client) => client.id) ?? []);
    }
  };
  const handleSelectAllSpecialties = () => {
    if (selectedIdSpecialties.length === specialties?.length) {
      setSelectedIdSpecialties([]);
    } else {
      setSelectedIdSpecialties(specialties?.map((specialtie) => specialtie.id) ?? []);
    }
  };
  const handleSelectAllCorrespondences = () => {
    if (selectedIdCorrespondences.length === correspondences?.length) {
      setSelectedIdCorrespondences([]);
    } else {
      setSelectedIdCorrespondences(
        correspondences?.map((correspondence) => correspondence.id) ?? [],
      );
    }
  };

  const steps = [
    {
      target: '#start-item',
      content:
        'Os perfis correspondem aos acessos e preferências que o seu colaborador pode ter dentro da sua empresa, a partir desse menu é possível adicionar novos perfis, editar ou visualizar os perfis já cadastrados.',
      disableBeacon: true,
    },
    {
      target: '#select-profile-type',
      content: 'Aqui você pode selecionar o tipo de perfil que deseja gerenciar.',
    },
    {
      target: '#add-profile',
      content:
        'Para adicionar um perfil, clique neste botão. O tipo do perfil será definido com base na seleção acima. Em seguida, preencha o nome do perfil e clique no botão "+" novamente.',
    },
    {
      target: '#search-profile',
      content: 'Para pesquisar um perfil basta clicar na lupa e digitar o nome do perfil.',
    },
    {
      target: '#delete-profile',
      content:
        'Para excluir um perfil basta clicar na lixeira e confirmar clicando no botão excluir.',
    },
  ];

  useEffect(() => {
    app.setLoading(isLoadingProfiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingProfiles]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsChangingProfile(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [isChangingProfile]);

  useEffect(() => {
    setMessageError('');
  }, [selectedProfile]);

  useEffect(() => {
    setEnableClientsLoad(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user?.tutorialProgress?.some((p) => p === TutorialType.PROFILES)) {
      setIsRunningTutorial(true);
      setSelectedTypeProfile(ProfileType.REQUESTER);
    }
  }, [user.tutorialProgress]);

  return (
    <>
      <NavHeader typeHeader="company" />
      <JoyrideTour
        type={TutorialType.PROFILES}
        steps={steps}
        run={isRunningTutorial}
        onCallbackFinished={() => {
          setIsRunningTutorial(false);
          setSelectedTypeProfile(undefined);
        }}
      />
      <Body>
        <MenuOptionLeftCompany />
        <Container>
          <Header>
            <TitlePage
              title="Perfis"
              onRequestOpen={() => {
                setIsRunningTutorial(true);
                setSelectedTypeProfile(ProfileType.REQUESTER);
              }}
              id="start-item"
            />
          </Header>
          <Content>
            <div className="menuProfiles">
              <div className="containerTypeProfile">
                <DropDownList
                  width="15rem"
                  disabled={isRunningTutorial}
                  defaultValue={
                    selectedTypeProfile !== undefined ? selectedTypeProfile.toString() : ''
                  }
                  elementId="select-profile-type"
                  itens={Object.keys(ProfileType)
                    .filter((p) => !isNaN(Number(p)))
                    .map((p) => ({
                      id: p,
                      value: labelProfileType[p as unknown as ProfileType],
                    }))}
                  placeholderTitle="Selecione o tipo perfil"
                  onChange={(value) => {
                    setSelectedTypeProfile(Number(value));
                    setFilterProfiles('');
                  }}
                  typeReturn="id"
                />
                {selectedTypeProfile === ProfileType.FINANCIAL && (
                  <div className="title-financial">
                    <Tooltip
                      disableInteractive
                      title="Esse perfil poderá visualizar todos os relatórios de pagamentos e informações detalhadas das solicitações.
                    E apenas usuários vinculados a esse perfil poderão alterar status de pagamentos e anexar os respectivos comprovantes.">
                      <ErrorIcon style={{ color: colors.primary }} />
                    </Tooltip>
                  </div>
                )}
              </div>
              {selectedTypeProfile !== undefined && (
                <div
                  style={{
                    margin: '1rem 1rem 0 1rem',
                    display: 'flex',
                  }}>
                  <div className="contentButtons">
                    <div id="add-profile">
                      <InputAdd
                        defaultShowInput={false}
                        placeholder="Nome do perfil"
                        onAdd={(value) => handleAddProfile(value, selectedTypeProfile)}
                        messageError={messageError}
                        onChangeIfShowInput={() => {
                          setMessageError('');
                        }}
                      />
                    </div>
                    <div id="search-profile">
                      <InputFilter
                        onSearch={setFilterProfiles}
                        placeholder="Pesquisar perfil"
                        defaultShowInput={false}
                      />
                    </div>
                  </div>
                </div>
              )}
              {isRunningTutorial && selectedTypeProfile === ProfileType.REQUESTER && (
                <div className="containerProfile">
                  <div className="contentProfile">
                    {requestersToTutorial.map((profile) => (
                      <Tooltip
                        disableInteractive
                        title={profile.name}
                        placement="left"
                        key={profile.id}>
                        <div className="profileItem">
                          <button
                            type="button"
                            onClick={() => handleSelectProfile(profile.id)}
                            className={
                              selectedProfile?.id === profile?.id
                                ? 'profileNameSelected'
                                : 'profileName'
                            }>
                            {profile.name.trim()}
                          </button>
                          <button
                            type="button"
                            className="delete"
                            disabled
                            onClick={() => handleDeleteProfile(profile.id)}>
                            <DeleteIcon id="delete-profile" />
                          </button>
                        </div>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              )}
              {!isRunningTutorial && selectedTypeProfile === ProfileType.REQUESTER && (
                <div className="containerProfile">
                  <div className="contentProfile">
                    {profiles
                      .filter((p) => slugify(p.name).includes(slugify(filterProfiles)))
                      .sort((a, b) => a.name.trim().localeCompare(b.name.trim()))
                      .filter((r) => r.type === ProfileType.REQUESTER)
                      .map((profile) => (
                        <Tooltip
                          disableInteractive
                          title={profile.name}
                          placement="left"
                          key={profile.id}>
                          <div className="profileItem">
                            <button
                              type="button"
                              onClick={() => handleSelectProfile(profile.id)}
                              className={
                                selectedProfile?.id === profile?.id
                                  ? 'profileNameSelected'
                                  : 'profileName'
                              }>
                              {profile.name.trim()}
                            </button>
                            <button
                              type="button"
                              className="delete"
                              onClick={() => handleDeleteProfile(profile.id)}>
                              <DeleteIcon />
                            </button>
                          </div>
                        </Tooltip>
                      ))}
                  </div>
                </div>
              )}
              {selectedTypeProfile === ProfileType.APPROVER && (
                <div className="containerProfile">
                  <div className="contentProfile">
                    {profiles
                      .filter((p) => slugify(p.name).includes(slugify(filterProfiles)))
                      .sort((a, b) => a.name.trim().localeCompare(b.name.trim()))
                      .filter((r) => r.type === ProfileType.APPROVER)
                      .map((profile) => (
                        <Tooltip
                          disableInteractive
                          title={profile.name}
                          placement="left"
                          key={profile.id}>
                          <div className="profileItem">
                            <button
                              type="button"
                              onClick={() => handleSelectProfile(profile.id)}
                              className={
                                selectedProfile?.id === profile?.id
                                  ? 'profileNameSelected'
                                  : 'profileName'
                              }>
                              {profile.name.trim()}
                            </button>
                            <button
                              type="button"
                              className="delete"
                              onClick={() => handleDeleteProfile(profile.id)}>
                              <DeleteIcon />
                            </button>
                          </div>
                        </Tooltip>
                      ))}
                  </div>
                </div>
              )}
              {selectedTypeProfile === ProfileType.OPERATOR && (
                <div className="containerProfile">
                  <div className="contentProfile">
                    {profiles
                      .filter((p) => slugify(p.name).includes(slugify(filterProfiles)))
                      .sort((a, b) => a.name.trim().localeCompare(b.name.trim()))
                      .filter((r) => r.type === ProfileType.OPERATOR)
                      .map((profile) => (
                        <Tooltip
                          disableInteractive
                          title={profile.name}
                          placement="left"
                          key={profile.id}>
                          <div className="profileItem">
                            <button
                              type="button"
                              onClick={() => handleSelectProfile(profile.id)}
                              className={
                                selectedProfile?.id === profile?.id
                                  ? 'profileNameSelected'
                                  : 'profileName'
                              }>
                              {profile.name.trim()}
                            </button>
                            <button
                              type="button"
                              className="delete"
                              onClick={() => handleDeleteProfile(profile.id)}>
                              <DeleteIcon />
                            </button>
                          </div>
                        </Tooltip>
                      ))}
                  </div>
                </div>
              )}
              {selectedTypeProfile === ProfileType.FINANCIAL && (
                <div className="containerProfile">
                  <div className="contentProfile">
                    {profiles
                      .filter((p) => slugify(p.name).includes(slugify(filterProfiles)))
                      .sort((a, b) => a.name.trim().localeCompare(b.name.trim()))
                      .filter((r) => r.type === ProfileType.FINANCIAL)
                      .map((profile) => (
                        <Tooltip
                          disableInteractive
                          title={profile.name}
                          placement="left"
                          key={profile.id}>
                          <div className="profileItem">
                            <button
                              type="button"
                              onClick={() => setSelectedProfile(profile)}
                              className={
                                selectedProfile?.id === profile?.id
                                  ? 'profileNameSelected'
                                  : 'profileName'
                              }>
                              {profile.name.trim()}
                            </button>
                            <button
                              type="button"
                              className="delete"
                              onClick={() => handleDeleteProfile(profile.id)}>
                              <DeleteIcon />
                            </button>
                          </div>
                        </Tooltip>
                      ))}
                  </div>
                </div>
              )}
            </div>
            {isChangingProfile ? (
              <Grid container justifyContent="center" alignContent="center">
                <Typography align="center" color={colors.primary}>
                  <CircularProgress size={15} /> Buscando...
                </Typography>
              </Grid>
            ) : selectedProfile ? (
              <div className="configurationProfile">
                <div className="nameProfile">
                  <Tooltip disableInteractive title={selectedProfile.name} placement="top-start">
                    <h1>{selectedProfile.name}</h1>
                  </Tooltip>
                </div>
                <div className="contentAccordions">
                  <AccordionExpanded
                    title="Clientes"
                    icon
                    className="accordion"
                    activeButton
                    defaultOpen
                    onCallback={handleSelectAllClients}>
                    <>
                      <div style={{ marginLeft: '0.5rem', marginBottom: '1rem' }}>
                        <InputFilter onSearch={setFilterClients} placeholder="Pesquisar" />
                      </div>
                      <ContainerMap>
                        {clients &&
                          clients
                            .filter((c) =>
                              c.name.toLowerCase().includes(filterClients.toLowerCase()),
                            )
                            .sort((a, b) => a.name.trim().localeCompare(b.name.trim()))
                            .map((client) => (
                              <Tooltip
                                disableInteractive
                                key={client.id}
                                title={client.name}
                                followCursor>
                                <ContentMap>
                                  <Checkbox
                                    id={client.id}
                                    className="checkbox"
                                    onChange={() => {
                                      const isExists = selectedIdClients.some(
                                        (c) => c === client.id,
                                      );
                                      if (!isExists) {
                                        setSelectedIdClients((prev) => [...prev, client.id]);
                                      } else {
                                        const newSelectedIdClients = selectedIdClients.filter(
                                          (c) => c !== client.id,
                                        );
                                        setSelectedIdClients(newSelectedIdClients);
                                      }
                                    }}
                                    checked={selectedIdClients.some((c) => c === client.id)}
                                  />
                                  <label htmlFor={client.id}>{client.name.trim()}</label>
                                </ContentMap>
                              </Tooltip>
                            ))}
                      </ContainerMap>
                    </>
                  </AccordionExpanded>
                </div>
                {selectedProfile.type !== ProfileType.APPROVER &&
                  selectedProfile.type !== ProfileType.FINANCIAL && (
                    <>
                      <AccordionExpanded
                        title="Especialidades"
                        icon
                        activeButton
                        defaultOpen
                        onCallback={handleSelectAllSpecialties}>
                        <>
                          <div style={{ marginLeft: '0.5rem', marginBottom: '1rem' }}>
                            <InputFilter onSearch={setFilterSpecialties} placeholder="Pesquisar" />
                          </div>
                          <ContainerMap>
                            {specialties &&
                              specialties
                                .filter((s) =>
                                  s.name.toLowerCase().includes(filterSpecialties.toLowerCase()),
                                )
                                .sort((a, b) => a.name.trim().localeCompare(b.name.trim()))
                                .map((specialtie) => (
                                  <Tooltip
                                    disableInteractive
                                    key={specialtie.id}
                                    title={specialtie.name}
                                    followCursor>
                                    <ContentMap>
                                      <Checkbox
                                        id={specialtie.id}
                                        className="checkbox"
                                        onChange={() => {
                                          const isExists = selectedIdSpecialties.some(
                                            (s) => s === specialtie.id,
                                          );
                                          if (!isExists) {
                                            setSelectedIdSpecialties((prev) => [
                                              ...prev,
                                              specialtie.id,
                                            ]);
                                          } else {
                                            const newSelectedIdSpecialties =
                                              selectedIdSpecialties.filter(
                                                (s) => s !== specialtie.id,
                                              );
                                            setSelectedIdSpecialties(newSelectedIdSpecialties);
                                          }
                                        }}
                                        checked={selectedIdSpecialties.some(
                                          (s) => s === specialtie.id,
                                        )}
                                      />
                                      <label htmlFor={specialtie.id}>
                                        {specialtie.name.trim()}
                                      </label>
                                    </ContentMap>
                                  </Tooltip>
                                ))}
                          </ContainerMap>
                        </>
                      </AccordionExpanded>
                      <AccordionExpanded
                        title="Serviços"
                        icon
                        activeButton
                        defaultOpen
                        onCallback={handleSelectAllCorrespondences}>
                        <>
                          <div style={{ marginLeft: '0.5rem', marginBottom: '1rem' }}>
                            <InputFilter
                              onSearch={setFilterCorrespondences}
                              placeholder="Pesquisar"
                            />
                          </div>
                          <ContainerMap>
                            {correspondences &&
                              correspondences
                                .filter((c) =>
                                  c.name
                                    .toLowerCase()
                                    .includes(filterCorrespondences.toLowerCase()),
                                )
                                .sort((a, b) => a.name.trim().localeCompare(b.name.trim()))
                                .map((correspondence) => (
                                  <Tooltip
                                    disableInteractive
                                    key={correspondence.id}
                                    title={correspondence.name}
                                    followCursor>
                                    <ContentMap>
                                      <Checkbox
                                        id={correspondence.id}
                                        className="checkbox"
                                        onChange={() => {
                                          const isExists = selectedIdCorrespondences.some(
                                            (c) => c === correspondence.id,
                                          );
                                          if (!isExists) {
                                            setSelectedIdCorrespondences((prev) => [
                                              ...prev,
                                              correspondence.id,
                                            ]);
                                          } else {
                                            const newSelectedIdCorrespondences =
                                              selectedIdCorrespondences.filter(
                                                (c) => c !== correspondence.id,
                                              );
                                            setSelectedIdCorrespondences(
                                              newSelectedIdCorrespondences,
                                            );
                                          }
                                        }}
                                        checked={selectedIdCorrespondences.some(
                                          (c) => c === correspondence.id,
                                        )}
                                      />
                                      <label
                                        htmlFor={correspondence.id}
                                        title={correspondence.name}>
                                        {correspondence.name.trim()}
                                      </label>
                                    </ContentMap>
                                  </Tooltip>
                                ))}
                          </ContainerMap>
                        </>
                      </AccordionExpanded>
                      {selectedProfile.type !== ProfileType.REQUESTER && (
                        <AccordionExpanded title="Valor limite" icon>
                          <ContainerMap>
                            <div className="limitValueContainer">
                              <div className="values">
                                <Controller
                                  control={control}
                                  name="minValue"
                                  render={({ field: { onChange } }) => (
                                    <CurrencyInput
                                      value={minValueInput}
                                      fullWidth
                                      variant="outlined"
                                      size="small"
                                      className="inputSuggestionValue"
                                      placeholder="Informe o valor"
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment sx={{ pl: 1 }} position="start">
                                            R$
                                          </InputAdornment>
                                        ),
                                      }}
                                      onChange={onChange}
                                    />
                                  )}
                                />
                                <span>até</span>
                                <Controller
                                  control={control}
                                  name="maxValue"
                                  render={({ field: { onChange } }) => (
                                    <CurrencyInput
                                      value={maxValueInput}
                                      fullWidth
                                      variant="outlined"
                                      size="small"
                                      className="inputSuggestionValue"
                                      placeholder="Informe o valor"
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment sx={{ pl: 1 }} position="start">
                                            R$
                                          </InputAdornment>
                                        ),
                                      }}
                                      onChange={onChange}
                                    />
                                  )}
                                />
                              </div>
                              {maxValueInput < minValueInput && (
                                <span className="fieldError">
                                  <ErrorIcon fontSize="inherit" /> Valor máximo deve ser maior que o
                                  valor mínimo
                                </span>
                              )}
                            </div>
                          </ContainerMap>
                          <div className="container-Approval-Value">
                            <h3>Esse perfil terá que passar pela aprovação</h3>
                            <div className="dropDownApprovalValue">
                              <FormControl fullWidth>
                                <Select
                                  open={isOpen}
                                  onOpen={handleOpen}
                                  onClose={handleClose}
                                  name="selectApproverValue"
                                  value={
                                    selectApproverLimitValue === ''
                                      ? 'perfil'
                                      : selectApproverLimitValue
                                  }
                                  classes={{ select: 'select-style' }}
                                  onChange={handleSelectApproverLimitValue}
                                  MenuProps={MenuProps}
                                  displayEmpty>
                                  <MenuItem defaultValue="perfil" value="perfil" disabled>
                                    Selecione o perfil
                                  </MenuItem>

                                  {profiles
                                    .filter((p) => p.type === ProfileType.APPROVER)
                                    .map((profile) => (
                                      <MenuItem key={profile.id} value={profile.id}>
                                        {profile.name}
                                      </MenuItem>
                                    ))}
                                </Select>
                              </FormControl>
                            </div>
                          </div>
                          <div className="containerMapApprovers">
                            {approverLimit !== undefined && (
                              <div key={approverLimit.id}>
                                <div className="approvers">
                                  {approverLimit.name}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setApprovers((state) =>
                                        state.filter((s) => s.id !== approverLimit.id),
                                      );
                                      setApproverLimit(undefined);
                                    }}>
                                    <CloseIcon fontSize="small" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </AccordionExpanded>
                      )}
                      {selectedProfile.type !== ProfileType.OPERATOR && (
                        <AccordionExpanded title="Aprovação" icon defaultOpen>
                          <div className="containerApproval">
                            <h1>Esse perfil terá que passar pela aprovação</h1>
                            <div className="dropDown">
                              <DropDownList
                                isSearchable
                                itens={
                                  profiles
                                    .filter(
                                      (p) =>
                                        p.type === ProfileType.APPROVER &&
                                        approvers.every((a) => a.id !== p.id),
                                    )
                                    .sort((a, b) => a.name.trim().localeCompare(b.name.trim()))
                                    .map((profile) => ({ id: profile.id, value: profile.name })) ||
                                  []
                                }
                                placeholderTitle="Selecione o aprovador"
                                clearToSelect
                                typeReturn="id"
                                onChange={(e) => {
                                  const profileSelectedId = e as string;
                                  handleSelectApprovers(profileSelectedId);
                                }}
                              />
                            </div>
                          </div>
                          <div className="containerMapApprovers">
                            {approvers.map((approver) => (
                              <div key={approver.id}>
                                <div className="approvers">
                                  {approver.name}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setApprovers((state) =>
                                        state.filter((s) => s.id !== approver.id),
                                      );
                                    }}>
                                    <CloseIcon fontSize="small" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </AccordionExpanded>
                      )}
                    </>
                  )}
                <Button
                  type="button"
                  color="background_white"
                  background="primary"
                  dimensions={{ width: '12rem', height: '2rem' }}
                  style={{ alignSelf: 'flex-end' }}
                  onClick={handleUpdateProfile}
                  text="Salvar"
                />
              </div>
            ) : (
              selectedTypeProfile !== undefined && (
                <div className="profileEmpty">
                  <Typography align="center" color={colors.primary}>
                    Selecione ou crie um perfil para configurá-lo
                  </Typography>
                </div>
              )
            )}
          </Content>
        </Container>
      </Body>
      <ModalDeleteProfile
        isOpen={displayModalDeleteProfile}
        onRequestClose={() => {
          setDisplayModalDeleteProfile(false);
          setSelectedProfile(undefined);
        }}
        profiles={profiles}
        currentProfileId={selectedProfile?.id}
      />
    </>
  );
}
